import { useState } from 'react'
import { useAlerts, useAcknowledgeAlert, useGenerateActions } from '../hooks/useLLR'
import { useAILLRActions } from '../hooks/useAILLRActions'
import {
  Bell, Zap, TrendingUp, MapPin, FileText, Megaphone,
  Globe, Check, ChevronDown, ChevronUp, Sparkles, Loader2, Copy,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

function AIStar() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path d="M8 0L9.8 6.2L16 8L9.8 9.8L8 16L6.2 9.8L0 8L6.2 6.2L8 0Z" fill="currentColor" />
    </svg>
  )
}

const intensityConfig = {
  critical: { min: 80, color: 'border-l-brick bg-brick-light/40', badge: 'bg-brick text-white', label: 'Critique' },
  high: { min: 65, color: 'border-l-orange-500 bg-orange-50/40', badge: 'bg-orange-500 text-white', label: 'Élevé' },
  medium: { min: 50, color: 'border-l-gold bg-gold-light/40', badge: 'bg-gold text-onyx', label: 'Modéré' },
  low: { min: 0, color: 'border-l-ink-border bg-paper', badge: 'bg-ink-faint text-white', label: 'Faible' },
}

function getLevel(score: number) {
  if (score >= 80) return intensityConfig.critical
  if (score >= 65) return intensityConfig.high
  if (score >= 50) return intensityConfig.medium
  return intensityConfig.low
}

function CharCounter({ current, max }: { current: number; max: number }) {
  const over = current > max
  return (
    <span className={cn('text-[10px] font-mono', over ? 'text-brick font-semibold' : 'text-ink-faint')}>
      {current}/{max}
    </span>
  )
}

function LLRActionsPanel({
  actions,
  isLoading,
  error,
  intensityScore,
}: {
  actions: { gbp_post: string; ads_headlines: string[]; ads_descriptions: string[]; landing_brief: string } | null
  isLoading: boolean
  error: string | null
  intensityScore: number
}) {
  const [tab, setTab] = useState<'gbp' | 'ads' | 'landing'>('gbp')
  const [copied, setCopied] = useState(false)

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (isLoading) {
    return (
      <div className="border-t border-ink-border/30 px-5 py-4 bg-paper/50 grid grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-paper-warm rounded-lg h-24" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="border-t border-ink-border/30 px-5 py-4">
        <p className="text-xs text-brick">{error}</p>
      </div>
    )
  }

  if (!actions) return null

  const tabs = [
    { id: 'gbp' as const, label: 'Post GBP', icon: FileText },
    { id: 'ads' as const, label: 'Annonce Ads', icon: Megaphone },
    { id: 'landing' as const, label: 'Brief Landing', icon: Globe },
  ]

  return (
    <div className="border-t border-ink-border/30 px-5 py-4 bg-paper/50 animate-fade-in">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h4 className="font-serif text-sm font-bold text-ink">Actions IA recommandées</h4>
          <span className="bg-gold-light text-gold-dark text-[10px] font-medium px-2 py-0.5 rounded">
            Intensité {intensityScore}/100
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              tab === t.id ? 'bg-gold text-onyx' : 'bg-paper-warm text-ink-muted hover:bg-ink-border/30')}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* GBP Tab */}
      {tab === 'gbp' && (
        <div className="space-y-2">
          <div className="bg-white rounded-lg border border-ink-border p-3 text-sm text-ink whitespace-pre-wrap">
            {actions.gbp_post}
          </div>
          <div className="flex items-center justify-between">
            <CharCounter current={actions.gbp_post.length} max={150} />
            <button onClick={() => handleCopy(actions.gbp_post)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-brick bg-brick-light hover:bg-brick-light/70 rounded-lg transition-colors">
              {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copié' : 'Copier le post'}
            </button>
          </div>
        </div>
      )}

      {/* Ads Tab */}
      {tab === 'ads' && (
        <div className="space-y-3">
          <div>
            <p className="text-[10px] text-ink-faint uppercase tracking-wider mb-1.5">Titres</p>
            {actions.ads_headlines.map((h, i) => (
              <div key={i} className={cn('flex items-center justify-between px-3 py-2 rounded-lg border mb-1.5',
                h.length > 30 ? 'border-brick/30 bg-brick-light/30' : 'border-ink-border bg-white')}>
                <span className="text-sm text-ink">{h}</span>
                <CharCounter current={h.length} max={30} />
              </div>
            ))}
          </div>
          <div>
            <p className="text-[10px] text-ink-faint uppercase tracking-wider mb-1.5">Descriptions</p>
            {actions.ads_descriptions.map((d, i) => (
              <div key={i} className={cn('flex items-center justify-between px-3 py-2 rounded-lg border mb-1.5',
                d.length > 90 ? 'border-brick/30 bg-brick-light/30' : 'border-ink-border bg-white')}>
                <span className="text-sm text-ink">{d}</span>
                <CharCounter current={d.length} max={90} />
              </div>
            ))}
          </div>
          <button onClick={() => handleCopy([...actions.ads_headlines, ...actions.ads_descriptions].join('\n'))}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-brick bg-brick-light hover:bg-brick-light/70 rounded-lg transition-colors">
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copié' : 'Copier tout'}
          </button>
        </div>
      )}

      {/* Landing Tab */}
      {tab === 'landing' && (
        <div className="space-y-2">
          <div className="bg-white rounded-lg border border-ink-border p-3 text-sm text-ink leading-relaxed whitespace-pre-wrap">
            {actions.landing_brief}
          </div>
          <button onClick={() => handleCopy(actions.landing_brief)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-brick bg-brick-light hover:bg-brick-light/70 rounded-lg transition-colors">
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copié' : 'Copier le brief'}
          </button>
        </div>
      )}
    </div>
  )
}

function AlertCard({ alert, onAck, alertIdWithAI, onToggleAI, aiActions, aiLoading, aiError }: {
  alert: any
  onAck: (id: string) => void
  alertIdWithAI: string | null
  onToggleAI: (alert: any) => void
  aiActions: any
  aiLoading: boolean
  aiError: string | null
}) {
  const [expanded, setExpanded] = useState(false)
  const cfg = getLevel(alert.intensity_score)
  const showAIPanel = alertIdWithAI === alert.id

  return (
    <div className={cn('border-l-4 rounded-card bg-white border border-ink-border overflow-hidden', cfg.color)}>
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className={cn('text-xs font-bold px-2.5 py-0.5 rounded-full', cfg.badge)}>
              {alert.intensity_score.toFixed(0)}/100
            </span>
            <span className="text-sm font-semibold text-ink truncate">{alert.keyword}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {alert.status === 'unread' && (
              <button onClick={() => onAck(alert.id)}
                className="text-xs text-gold hover:text-gold-dark font-medium flex items-center gap-1">
                <Check size={14} /> Lu
              </button>
            )}
            <span className="text-[11px] text-ink-faint">
              {new Date(alert.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-ink-muted mb-3">
          {alert.radar_name && (
            <span className="flex items-center gap-1"><MapPin size={12} /> {alert.radar_name}</span>
          )}
          {alert.variation_pct && (
            <span className="flex items-center gap-1 text-emerald-600 font-medium">
              <TrendingUp size={12} /> +{alert.variation_pct.toFixed(0)}%
            </span>
          )}
        </div>

        {alert.trigger_reasons?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {alert.trigger_reasons.map((r: string, i: number) => (
              <span key={i} className="text-[11px] bg-paper-warm text-ink-muted px-2 py-0.5 rounded-full">{r}</span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          {alert.intensity_score >= 50 && (
            <button onClick={() => onToggleAI(alert)}
              className={cn('flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                showAIPanel ? 'bg-gold text-onyx' : 'bg-gold text-onyx hover:bg-gold/90')}>
              {aiLoading && showAIPanel ? <Loader2 size={12} className="animate-spin" /> : <AIStar />}
              Actions IA
            </button>
          )}
          {alert.gbp_post_draft && (
            <button onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 rounded-lg bg-paper-warm px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-ink-border/30 transition-colors">
              <FileText size={14} /> Contenus existants {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          )}
        </div>
      </div>

      {/* AI Actions Panel */}
      {showAIPanel && (
        <LLRActionsPanel
          actions={aiActions}
          isLoading={aiLoading}
          error={aiError}
          intensityScore={alert.intensity_score}
        />
      )}

      {/* Old expanded content */}
      {expanded && alert.gbp_post_draft && (
        <div className="border-t border-ink-border/30 px-5 py-4 bg-paper/50 space-y-4">
          <div>
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-2">Post GBP généré</p>
            <div className="bg-white rounded-lg border border-ink-border p-3 text-sm text-ink whitespace-pre-wrap">{alert.gbp_post_draft}</div>
          </div>
          {alert.ads_headline && (
            <div>
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-2">Brouillon Ads</p>
              <div className="bg-white rounded-lg border border-ink-border p-3 text-sm text-ink whitespace-pre-wrap">{alert.ads_headline}</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}


export function AlertCenterPage() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const { data, isLoading } = useAlerts({ status: statusFilter })
  const ack = useAcknowledgeAlert()
  const ai = useAILLRActions()
  const [alertIdWithAI, setAlertIdWithAI] = useState<string | null>(null)

  const alerts = data?.data ?? []
  const total = data?.meta?.total ?? 0
  const filters = [
    { value: undefined, label: 'Toutes' },
    { value: 'unread', label: 'Non lues' },
    { value: 'read', label: 'Lues' },
    { value: 'actioned', label: 'Traitées' },
  ]

  const handleToggleAI = (alert: any) => {
    if (alertIdWithAI === alert.id) {
      setAlertIdWithAI(null)
      ai.reset()
      return
    }
    ai.reset()
    setAlertIdWithAI(alert.id)
    ai.generate({
      keyword: alert.keyword,
      location: alert.radar_name || 'France',
      intensity_score: alert.intensity_score,
      business_name: 'Mon entreprise',
      business_type: 'entreprise locale',
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Centre d'alertes</h1>
        <p className="text-ink-muted mt-1">Pics de demande détectés et actions IA</p>
      </div>

      <div className="flex items-center gap-2">
        {filters.map((f) => (
          <button key={f.label} onClick={() => setStatusFilter(f.value)}
            className={cn('px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              statusFilter === f.value ? 'bg-gold text-onyx' : 'bg-paper-warm text-ink-muted hover:bg-ink-border/30')}>
            {f.label}
          </button>
        ))}
        <span className="text-sm text-ink-faint ml-2">{total} alerte(s)</span>
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-12 text-ink-faint">Chargement des alertes…</div>
        ) : !alerts.length ? (
          <div className="text-center py-12">
            <Bell size={48} className="mx-auto text-ink-border mb-3" />
            <p className="text-ink-muted">Aucune alerte</p>
            <p className="text-sm text-ink-faint mt-1">Les alertes apparaîtront ici quand un pic de demande sera détecté.</p>
          </div>
        ) : (
          alerts.map((a: any) => (
            <AlertCard
              key={a.id}
              alert={a}
              onAck={(id) => ack.mutate(id)}
              alertIdWithAI={alertIdWithAI}
              onToggleAI={handleToggleAI}
              aiActions={alertIdWithAI === a.id ? ai.actions : null}
              aiLoading={alertIdWithAI === a.id && ai.isLoading}
              aiError={alertIdWithAI === a.id ? ai.error : null}
            />
          ))
        )}
      </div>
    </div>
  )
}
